import * as z from "zod/v4";
import { vehiclePlateInvalidMessage, vehiclePlateRequiredMessage } from "../../i18n/server-errors";

/**
 * Allowed characters: letters (upper & lower), digits, #, &, @, $, /, !, ¡, +, =.
 * Length: 1–10.
 */
const LICENSE_PLATE_CHARS = "A-Za-z0-9#&@$/!¡+=";
const ESCAPED_LICENSE_PLATE_CHARS = LICENSE_PLATE_CHARS.replace(/[\/\\^$*+?.()|[\]{}]/g, "\\$&");

export const LICENSE_PLATE_MIN_LENGTH = 1;
export const LICENSE_PLATE_MAX_LENGTH = 10;

const plateRequiredError = {
	message: "License plate is required",
	params: {
		i18n: vehiclePlateRequiredMessage,
	},
} as const;

const plateInvalidError = {
	message: `Please enter a valid license plate (up to ${LICENSE_PLATE_MAX_LENGTH} characters)`,
	params: {
		i18n: vehiclePlateInvalidMessage,
	},
} as const;

/** Validation regex (safe for both JS + HTML pattern usage) */
export const LICENSE_PLATE_REGEX = new RegExp(
	`^[${ESCAPED_LICENSE_PLATE_CHARS}]{${LICENSE_PLATE_MIN_LENGTH},${LICENSE_PLATE_MAX_LENGTH}}$`,
);

/**
 * Cleans a license plate string by removing invalid characters and formatting it consistently.
 */
export function cleanPlate(plate: string): string {
	if (!plate) {
		return "";
	}

	return plate
		.replace(new RegExp(`[^${ESCAPED_LICENSE_PLATE_CHARS}]`, "g"), "")
		.toUpperCase()
		.trim();
}

/**
 * Zod validation schema for license plates.
 */
export const zodPlateValidation = z
	.string()
	.transform(cleanPlate)
	.superRefine((plate, ctx) => {
		if (plate.length < LICENSE_PLATE_MIN_LENGTH) {
			ctx.addIssue({
				code: "custom",
				...plateRequiredError,
			});
			return;
		}

		if (plate.length > LICENSE_PLATE_MAX_LENGTH || !LICENSE_PLATE_REGEX.test(plate)) {
			ctx.addIssue({
				code: "custom",
				...plateInvalidError,
			});
		}
	});

export const commaSeperatedPlateSchema = (t: (k: string) => string) =>
	z
		.string()
		.min(1)
		.transform((str) =>
			str
				.split(",")
				.map((p) => cleanPlate(p))
				.filter(Boolean)
				.join(","),
		)
		.refine(
			(plates) => {
				const plateList = plates.split(",");
				if (plateList.length < 1 || plateList.length > 4) {
					return false;
				}
				return plateList.every((p) => LICENSE_PLATE_REGEX.test(p));
			},
			{ message: t("formValidation:regex.multi-plate") },
		)
		.refine(
			(plates) => {
				const plateList = plates.split(",");
				const lower = plateList.map((p) => p.toLowerCase());
				return new Set(lower).size === lower.length;
			},
			{ message: t("formValidation:regex.multi-plate-duplicate") },
		);

export type PlateNumber = z.infer<typeof zodPlateValidation>;

export const VehicleSchema = z.object({
	plate: zodPlateValidation,
	state: z.string(),
});
